import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { useEffect, useState } from 'react'

import cn from '@/utilities/cn'

export type ComboBoxOption = {
  label: string
  displayText?: string
  icon?: any
  value: string
}

type ComboBoxProps = {
  error?: string | boolean
  name: string
  label?: string
  type?: string
  className?: string
  disabled?: boolean
  placeholder?: string
  onChange: (arg0: string) => void
  onSelect: (arg0: ComboBoxOption) => void
  options: ComboBoxOption[]
  value?: string | null
  isAsync?: boolean
  isLoading: boolean
  required?: boolean
  isAbsolute?: boolean
}

const ComboBox = ({
  error,
  className,
  label,
  name,
  required,
  value,
  isLoading,
  options,
  placeholder,
  onChange,
  isAsync,
  onSelect,
  disabled,
  isAbsolute = true
}: ComboBoxProps) => {
  const [query, setQuery] = useState('')
  const [selectedOption, setSelectedOption] = useState<ComboBoxOption | undefined>(
    options?.find(option => option.value === value)
  )
  const [filteredOptions, setFilteredOptions] = useState<ComboBoxOption[]>([])

  useEffect(() => {
    if (isAsync) {
      setFilteredOptions(options)
    } else {
      const filteredOptions =
        query === ''
          ? options
          : options.filter(option => {
              return option.label.toLowerCase().includes(query.toLowerCase())
            })
      setFilteredOptions(filteredOptions)
    }
  }, [options.length, query, isAsync, options])

  useEffect(() => {
    setSelectedOption(options?.find(option => option.value === value))
  }, [options, value])

  const onChangeOption = (value: string) => {
    setQuery(value)
    onChange(value)
  }

  const onSelectOption = (option: ComboBoxOption) => {
    setSelectedOption(option)
    onSelect(option)
  }

  const compareOption = (a: ComboBoxOption | null, b: ComboBoxOption | null): boolean =>
    a?.value?.toString()?.toLowerCase() === b?.value?.toString()?.toLowerCase()

  return (
    <Combobox
      disabled={disabled}
      className={className}
      as="div"
      by={compareOption}
      value={selectedOption || null}
      onChange={onSelectOption}
    >
      <Label htmlFor={name} className={cn(className, 'block text-sm font-medium leading-6 text-zinc-900')}>
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      <div className="relative mt-2">
        <div className="flex items-center">
          <ComboboxInput
            className={cn(
              disabled ? 'bg-zinc-50' : '',
              error ? 'ring-red-300 focus:ring-red-600' : 'ring-zinc-300 focus:ring-brand-400',
              'w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 focus:ring-2 focus:ring-inset focus:ring-brand-400 sm:text-sm sm:leading-6'
            )}
            onChange={(event: { target: { value: string } }) => onChangeOption(event.target.value)}
            displayValue={(option: ComboBoxOption) => option?.label}
            placeholder={placeholder}
            autoComplete={'off'}
          />
          {isLoading && (
            <svg
              className="absolute inset-y-0 right-[30px] top-[8px] flex h-5 w-5 animate-spin items-center text-zinc-500 focus:outline-none"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-zinc-400" aria-hidden="true" />
          </ComboboxButton>
        </div>
        {filteredOptions?.length > 0 && (
          <ComboboxOptions
            className={cn(
              'z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
              isAbsolute ? 'absolute' : 'relative'
            )}
          >
            {filteredOptions.map(option => (
              <ComboboxOption
                key={option.value}
                value={option}
                className={({ focus }: { focus: boolean }) =>
                  cn(
                    'relative cursor-default select-none py-2 pl-3 pr-9',
                    focus ? 'bg-brand-400 text-white' : 'text-zinc-900'
                  )
                }
              >
                {({ focus, selected }: { focus: boolean; selected: boolean }) => (
                  <>
                    <div className="flex items-center">
                      <div className={cn(focus ? '[&>*]:!text-white' : 'text-black')}>{option.icon}</div>
                      <div>
                        <p className={cn('ml-2 truncate', selected && 'font-semibold')}>{option.label?.toString()}</p>
                        <p className={cn('ml-2 truncate text-zinc-500', focus ? 'text-blue-200' : 'text-zinc-500')}>
                          {option.displayText?.toString()}
                        </p>
                      </div>
                    </div>

                    {selected && (
                      <span
                        className={cn(
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                          focus ? 'text-white' : 'text-brand-400'
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  )
}
export default ComboBox
